/**
 * The trail above a page card, in the two forms it takes: the items the
 * <t-breadcrumbs> strip draws, and the BreadcrumbList schema.org entry search
 * engines read the same trail from. Both come out of one list, so they can't
 * drift apart -- Google only trusts the markup when it matches what's on the
 * page.
 */

/**
 * A vue-meta script entry with the trail as JSON-LD. Items without a route
 * (the current page, a crumb still loading) keep their name only -- the last
 * one is allowed to, and an item with no url is better than a wrong one.
 *
 * @param  {Vue}   vm     the page component, for the router and the locale prefix
 * @param  {Array} items  [{ name, to }] as passed to <t-breadcrumbs>
 * @return {Object|undefined} nothing while a name is still unknown
 */
export const breadcrumbsJsonLd = function breadcrumbListScriptEntry(vm, items) {
    if (!items || items.some(item => !item.name)) {
        return undefined;
    }

    const itemListElement = items.map((item, index) => {
        const entry = {
            '@type': 'ListItem',
            'position': index + 1,
            'name': item.name,
        };

        if (item.to) {
            const { href } = vm.$router.resolve(vm.$localizeRoute(item.to));
            entry.item = `${window.location.origin}${href}`;
        }

        return entry;
    });

    return {
        type: 'application/ld+json',
        json: {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement,
        },
    };
};

/**
 * The middle of a long identifier, for a crumb: the ends of an address or a
 * hash are what people recognise it by.
 */
export const shortenCrumb = function shortenLongIdentifier(value, edge = 6) {
    if (!value || value.length <= edge * 2 + 1) {
        return value;
    }

    return `${value.slice(0, edge)}…${value.slice(-edge)}`;
};
